import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Option,
  Card,
  CardContent,
  RadioGroup,
  Radio,
  Chip,
  Stack,
  Alert,
} from '@mui/joy';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../lib/api';
import { Info as InfoIcon } from '@phosphor-icons/react';

interface AISettings {
  tone?: string;
  style?: string;
  keywords?: string[];
}

interface SettingsTabProps {
  slug: string;
  template: string;
  aiSettings?: AISettings;
  contentBrief?: string;
  manifest: any;
  onChange: (field: string, value: any) => void;
  siteId?: number;
  pageId?: number;
  currentParent?: number | null;
}

export const SettingsTab: React.FC<SettingsTabProps> = ({
  slug,
  template,
  aiSettings,
  contentBrief,
  manifest,
  onChange,
  siteId,
  pageId,
  currentParent,
}) => {
  const templates = manifest?.templates || {};
  const availableTemplates = Object.entries(templates).map(([key, config]: any) => ({
    key,
    name: config.name,
    description: config.description,
  }));

  const toneOptions = [
    { value: 'professional', label: 'Professional', description: 'Formal and authoritative' },
    { value: 'friendly', label: 'Friendly', description: 'Warm and approachable' },
    { value: 'conversational', label: 'Conversational', description: 'Casual and engaging' },
    { value: 'urgent', label: 'Urgent', description: 'Compelling and action-oriented' },
    { value: 'educational', label: 'Educational', description: 'Informative and helpful' },
  ];

  const styleOptions = [
    { value: 'concise', label: 'Concise', description: 'Brief and to the point' },
    { value: 'detailed', label: 'Detailed', description: 'Comprehensive and thorough' },
    { value: 'storytelling', label: 'Storytelling', description: 'Narrative and engaging' },
    { value: 'technical', label: 'Technical', description: 'Industry-specific terms' },
  ];

  // Fetch all pages from the same site for parent selection
  const { data: allPages } = useQuery({
    queryKey: ['cms-pages-for-parent', siteId],
    queryFn: async () => {
      if (!siteId) return [];
      const response = await api.get<any>(`/api/cms/sites/${siteId}/pages/`);
      return (response.data?.results || response.data || []);
    },
    enabled: !!siteId,
  });

  // Build hierarchical options for parent selector
  const parentPageOptions = useMemo(() => {
    if (!allPages) return [];
    
    const options: Array<{ value: number | null; label: string; disabled?: boolean }> = [
      { value: null, label: '(No parent - root level)' }
    ];
    
    // Build a set of all descendant IDs for the current page to prevent circular references
    const getDescendantIds = (parentId: number): Set<number> => {
      const descendants = new Set<number>();
      const addDescendants = (pid: number) => {
        const children = allPages.filter((p: any) => p.parent === pid);
        children.forEach((child: any) => {
          descendants.add(child.id);
          addDescendants(child.id);
        });
      };
      if (pageId) {
        addDescendants(parentId);
      }
      return descendants;
    };
    
    const currentPageDescendants = pageId ? getDescendantIds(Number(pageId)) : new Set();
    
    // Build a tree structure for indented display
    const buildOptions = (pages: any[], parentId: null | number = null, depth: number = 0, skipIds: Set<number> = new Set()) => {
      const children = pages.filter((p: any) => p.parent === parentId);
      
      children.forEach(page => {
        // Skip if this page is in the skip list (for preventing circular references)
        if (skipIds.has(page.id)) {
          return;
        }
        
        // Don't include the current page itself
        if (page.id !== pageId) {
          const indent = '  '.repeat(depth);
          const isDescendant = currentPageDescendants.has(page.id);
          options.push({
            value: page.id,
            label: `${indent}${page.seo_draft?.title || page.title || page.slug}${isDescendant ? ' (cannot select - would create circular reference)' : ''}`,
            disabled: isDescendant, // Disable if this page is a descendant of the current page
          });
          
          // Only recurse into children if this page isn't disabled
          if (!isDescendant) {
            buildOptions(pages, page.id, depth + 1, skipIds);
          }
        } else {
          // If this is the current page, skip it and all its descendants
          const newSkipIds = new Set(skipIds);
          newSkipIds.add(page.id);
          currentPageDescendants.forEach(id => newSkipIds.add(Number(id)));
          // Continue with siblings
          buildOptions(pages, Number(page.id), depth + 1, newSkipIds);
        }
      });
    };
    
    buildOptions(allPages);
    return options;
  }, [allPages, pageId]);

  const handleKeywordsChange = (value: string) => {
    const keywords = value.split(',').map(k => k.trim()).filter(k => k);
    onChange('ai_generation_settings', {
      ...aiSettings,
      keywords,
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography level="h3" sx={{ mb: 3 }}>Page Settings</Typography>

      <Stack spacing={3}>
        {/* Page Configuration */}
        <Card variant="outlined">
          <CardContent>
            <Typography level="title-lg" sx={{ mb: 2 }}>Page Configuration</Typography>
            
            <Stack spacing={2}>
              <FormControl>
                <FormLabel>Page Slug</FormLabel>
                <Input
                  value={slug}
                  onChange={(e) => onChange('slug', e.target.value)}
                  placeholder="page-slug"
                  startDecorator={<Typography level="body-sm">/</Typography>}
                />
                <Typography level="body-xs" sx={{ mt: 0.5 }}>
                  The URL path for this page. Use "index" for the homepage.
                </Typography>
              </FormControl>

              <FormControl>
                <FormLabel>Parent Page</FormLabel>
                <Select
                  value={currentParent}
                  onChange={(_, value) => onChange('parent', value)}
                  placeholder="Select parent page"
                >
                  {parentPageOptions.map(option => (
                    <Option 
                      key={option.value === null ? 'null' : option.value} 
                      value={option.value}
                      disabled={option.disabled}
                    >
                      {option.label}
                    </Option>
                  ))}
                </Select>
                <Typography level="body-xs" sx={{ mt: 0.5 }}>
                  Choose a parent to create a nested page structure (e.g., Services {'→'} Residential).
                </Typography>
              </FormControl>

              {/* Template selection removed - using universal sections */}
              <Alert color="primary" variant="soft">
                <InfoIcon />
                <Box>
                  <Typography level="body-sm">
                    <strong>Universal Sections Enabled</strong>
                  </Typography>
                  <Typography level="body-xs">
                    You can now add any section type to any page. Use the Content tab to build your page with the sections you need.
                  </Typography>
                </Box>
              </Alert>
            </Stack>
          </CardContent>
        </Card>

        {/* AI Generation Settings */}
        <Card variant="outlined">
          <CardContent>
            <Typography level="title-lg" sx={{ mb: 2 }}>AI Generation Settings</Typography>
            
            <Stack spacing={2}>
              <FormControl>
                <FormLabel>Content Tone</FormLabel>
                <RadioGroup
                  value={aiSettings?.tone || 'professional'}
                  onChange={(e) => onChange('ai_generation_settings', {
                    ...aiSettings,
                    tone: e.target.value,
                  })}
                >
                  {toneOptions.map((option) => (
                    <Radio
                      key={option.value}
                      value={option.value}
                      label={
                        <Box>
                          <Typography>{option.label}</Typography>
                          <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                            {option.description}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </RadioGroup>
              </FormControl>

              <FormControl>
                <FormLabel>Writing Style</FormLabel>
                <RadioGroup
                  value={aiSettings?.style || 'concise'}
                  onChange={(e) => onChange('ai_generation_settings', {
                    ...aiSettings,
                    style: e.target.value,
                  })}
                >
                  {styleOptions.map((option) => (
                    <Radio
                      key={option.value}
                      value={option.value}
                      label={
                        <Box>
                          <Typography>{option.label}</Typography>
                          <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                            {option.description}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </RadioGroup>
              </FormControl>

              <FormControl>
                <FormLabel>SEO Keywords</FormLabel>
                <Input
                  value={aiSettings?.keywords?.join(', ') || ''}
                  onChange={(e) => handleKeywordsChange(e.target.value)}
                  placeholder="roofing, repair, installation, commercial"
                />
                <Typography level="body-xs" sx={{ mt: 0.5 }}>
                  Separate keywords with commas. These will be used to optimize AI-generated content.
                </Typography>
              </FormControl>

              <FormControl>
                <FormLabel>Content Brief</FormLabel>
                <Textarea
                  value={contentBrief || ''}
                  onChange={(e) => onChange('content_brief', e.target.value)}
                  placeholder="Provide context about this page: What's the goal? Who's the audience? Any specific points to emphasize?"
                  minRows={4}
                />
                <Typography level="body-xs" sx={{ mt: 0.5 }}>
                  This brief helps the AI understand the context and generate more relevant content.
                </Typography>
              </FormControl>
            </Stack>
          </CardContent>
        </Card>

        {/* AI Keywords Display */}
        {aiSettings?.keywords && aiSettings.keywords.length > 0 && (
          <Card variant="outlined">
            <CardContent>
              <Typography level="title-md" sx={{ mb: 1 }}>Active Keywords</Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {aiSettings.keywords.map((keyword, index) => (
                  <Chip
                    key={index}
                    size="sm"
                    variant="soft"
                    color="primary"
                  >
                    {keyword}
                  </Chip>
                ))}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* Help Information */}
        <Alert
          startDecorator={<InfoIcon />}
          color="neutral"
        >
          <Box>
            <Typography level="title-sm">AI Content Generation Tips</Typography>
            <Typography level="body-sm" sx={{ mt: 1 }}>
              • Choose a tone and style that matches your brand voice
              <br />
              • Include relevant keywords for SEO optimization
              <br />
              • Provide a detailed content brief for better AI results
              <br />
              • All AI-generated content can be edited before publishing
            </Typography>
          </Box>
        </Alert>
      </Stack>
    </Box>
  );
};